/**
 * R5 — SezioneSpedizione.jsx
 *
 * Shipping configuration extracted from CommercialPanel (DX) into a
 * standalone component mounted in InputPane (SX) under the checklist.
 *
 * Also hosts the "Markup bilanciato" slider + traffic-light impact card.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, Target } from "lucide-react";
import { toast } from "sonner";
import { offersApi, offersClient } from "../lib/offersApi";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const ZONE_LABELS = {
  italia_centro: "Italia centro",
  italia_nord: "Italia nord",
  italia_sud_isole: "Italia sud / isole",
  estero_ue: "Estero UE",
  estero_extra_ue: "Estero extra UE",
};

function InfoTooltip({ text, testId }) {
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help" data-testid={testId} tabIndex={0}>
            <Info className="h-3 w-3" style={{ color: "var(--text-muted)" }} />
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-[300px] text-[12px] leading-snug whitespace-pre-line"
          style={{ background: "#0f172a", color: "white" }}
          side="right"
          sideOffset={6}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Traffic-light indicator for shipping impact on PV margin. */
function TrafficLight({ deltaPP, testId }) {
  // Rules: green ≤3pp, yellow 3–7pp, red >7pp
  const abs = Math.abs(deltaPP || 0);
  let tone, label, bg, color;
  if (abs <= 3) {
    tone = "green";
    label = "Impatto contenuto";
    bg = "#DCFCE7";
    color = "#15803D";
  } else if (abs <= 7) {
    tone = "yellow";
    label = "Impatto medio";
    bg = "#FEF3C7";
    color = "#B45309";
  } else {
    tone = "red";
    label = "Impatto alto";
    bg = "#FEE2E2";
    color = "#B91C1C";
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: bg, color }}
      data-testid={testId || `shipping-traffic-${tone}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

/**
 * @param props.sessionId string
 * @param props.session   the full session doc (reads shipping/margin_calc)
 * @param props.onUpdate  callback({shipping, margin_calc}) on server response
 */
export default function SezioneSpedizione({ sessionId, session, onUpdate }) {
  const [shipping, setShipping] = useState(
    session?.shipping || {
      onere: "cliente",
      destinazione_zona: "italia_centro",
    }
  );
  const [margin, setMargin] = useState(session?.margin_calc || null);
  const [shippingImpact, setShippingImpact] = useState(null);
  const [savingField, setSavingField] = useState(null);

  // Target PV margin (15–50%)
  const [targetPv, setTargetPv] = useState(28.57);
  const [balance, setBalance] = useState(null);
  const [fetchingBalance, setFetchingBalance] = useState(false);

  useEffect(() => {
    setShipping(
      session?.shipping || {
        onere: "cliente",
        destinazione_zona: "italia_centro",
      }
    );
    setMargin(session?.margin_calc || null);
  }, [session?.session_id, session?.updated_at]);

  const updateShipping = useCallback(
    async (patch) => {
      if (!sessionId) return;
      setSavingField("shipping");
      try {
        const next = { ...shipping, ...patch };
        setShipping(next);
        const res = await offersApi.updateShipping(sessionId, patch);
        setShipping(res.shipping);
        setShippingImpact(res.shipping_impact);
        setMargin(res.margin_calc);
        onUpdate?.({
          shipping: res.shipping,
          margin_calc: res.margin_calc,
          shipping_impact: res.shipping_impact,
        });
      } catch (e) {
        toast.error("Errore salvataggio spedizione");
      } finally {
        setSavingField(null);
      }
    },
    [sessionId, shipping, onUpdate]
  );

  // Fetch markup balance whenever target changes + shipping is "noi" with cost
  useEffect(() => {
    const canCompute =
      sessionId &&
      shipping.onere === "noi" &&
      Number(shipping.costo_stimato || 0) > 0;
    if (!canCompute) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    setFetchingBalance(true);
    offersClient
      .get(`/quote/${sessionId}/markup-shipping-balanced`, {
        params: { target_pv: targetPv },
      })
      .then((r) => {
        if (!cancelled) setBalance(r.data);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      })
      .finally(() => {
        if (!cancelled) setFetchingBalance(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, targetPv, shipping.onere, shipping.costo_stimato]);

  // Impact: how many pp is the margin degraded vs target because of shipping?
  const deltaPP =
    balance?.target_pv_pct != null && balance?.pv_effective_pct != null
      ? Number(balance.target_pv_pct) - Number(balance.pv_effective_pct)
      : shippingImpact?.impatto_percentuale || 0;

  return (
    <section
      className="space-y-3"
      data-testid="sezione-spedizione"
    >
      <div className="flex items-center justify-between">
        <div className="tq-section-label flex items-center gap-1">
          Spedizione
          <InfoTooltip
            text="Chi paga il trasporto e da dove spediamo. Se a carico nostro, il costo impatta il margine."
            testId="tooltip-shipping"
          />
        </div>
        {savingField === "shipping" && (
          <Loader2
            className="h-3 w-3 animate-spin"
            style={{ color: "var(--text-muted)" }}
          />
        )}
      </div>

      <div
        className="rounded-md p-2.5 space-y-2 text-[12px]"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 flex-1 cursor-pointer">
            <input
              type="radio"
              checked={shipping.onere === "cliente"}
              onChange={() => updateShipping({ onere: "cliente" })}
              data-testid="shipping-onere-cliente"
            />
            <span>Cliente</span>
          </label>
          <label className="flex items-center gap-1 flex-1 cursor-pointer">
            <input
              type="radio"
              checked={shipping.onere === "noi"}
              onChange={() => updateShipping({ onere: "noi" })}
              data-testid="shipping-onere-noi"
            />
            <span>A carico nostro</span>
          </label>
        </div>

        <select
          value={shipping.destinazione_zona || "italia_centro"}
          onChange={(e) =>
            updateShipping({ destinazione_zona: e.target.value })
          }
          className="w-full h-7 px-2 rounded text-[12px]"
          style={{
            border: "1px solid var(--border-default)",
            background: "var(--bg-surface)",
          }}
          data-testid="shipping-zona"
        >
          {Object.entries(ZONE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>

        <div className="grid grid-cols-2 gap-1.5">
          <input
            type="number"
            min="0"
            step="0.1"
            value={shipping.peso_stimato_kg || ""}
            onChange={(e) =>
              updateShipping({
                peso_stimato_kg: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
            placeholder="Peso kg"
            className="h-7 px-2 rounded text-[12px]"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="shipping-peso"
          />
          <input
            type="number"
            min="0"
            step="1"
            value={shipping.costo_stimato || ""}
            onChange={(e) =>
              updateShipping({
                costo_stimato: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
                costo_stimato_is_auto: false,
              })
            }
            placeholder="Costo €"
            className="h-7 px-2 rounded text-[12px] tq-cell-mono"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="shipping-costo"
          />
        </div>

        {shipping.costo_stimato_is_auto && (
          <div
            className="text-[10px]"
            style={{ color: "var(--text-muted)" }}
          >
            Stima: € {shipping.costo_stimato} (modificabile)
          </div>
        )}

        {shipping.onere === "cliente" && (
          <div
            className="text-[11px] italic"
            style={{ color: "var(--text-muted)" }}
          >
            Trasporto a carico cliente — non impatta margine.
          </div>
        )}
      </div>

      {/* Markup balanced slider — R5 P1 #5 */}
      {shipping.onere === "noi" && Number(shipping.costo_stimato || 0) > 0 && (
        <div
          className="rounded-md p-2.5 space-y-2"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-subtle)",
          }}
          data-testid="markup-balanced-panel"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Target
                className="h-3 w-3"
                style={{ color: "var(--accent-primary)" }}
              />
              <span
                className="text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: "var(--text-secondary)" }}
              >
                Margine target su PV
              </span>
              <InfoTooltip
                text="Sposta lo slider per scegliere il margine desiderato. Il sistema calcola di quanto aumentare il ricarico su merce per compensare il costo di spedizione."
                testId="tooltip-markup-target"
              />
            </div>
            <span
              className="text-[12px] tq-cell-mono font-semibold"
              style={{ color: "var(--text-primary)" }}
              data-testid="target-pv-value"
            >
              {targetPv.toFixed(1)}%
            </span>
          </div>
          <input
            type="range"
            min={15}
            max={50}
            step={0.5}
            value={targetPv}
            onChange={(e) => setTargetPv(parseFloat(e.target.value))}
            className="w-full"
            data-testid="target-pv-slider"
          />
          <div
            className="flex items-center justify-between text-[10px]"
            style={{ color: "var(--text-muted)" }}
          >
            <span>15%</span>
            <span>32.5%</span>
            <span>50%</span>
          </div>

          {/* Impact box: 3 metrics + traffic light */}
          <div
            className="rounded px-2 py-2 space-y-1.5"
            style={{
              background: "var(--bg-subtle)",
              border: "1px solid var(--border-subtle)",
            }}
            data-testid="shipping-impact-box"
          >
            <div className="flex items-center justify-between">
              <span
                className="text-[10px] uppercase tracking-wide font-medium"
                style={{ color: "var(--text-muted)" }}
              >
                Impatto spedizione
              </span>
              <TrafficLight deltaPP={deltaPP} />
            </div>
            {fetchingBalance && (
              <div
                className="text-[11px] flex items-center gap-1"
                style={{ color: "var(--text-muted)" }}
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                Calcolo in corso…
              </div>
            )}
            {!fetchingBalance && balance && (
              <div className="grid grid-cols-3 gap-1">
                <ImpactStat
                  label="Ricarico merce"
                  value={
                    balance.ricarico_pct != null
                      ? `${Number(balance.ricarico_pct).toFixed(1)}%`
                      : "—"
                  }
                  testId="impact-ricarico"
                />
                <ImpactStat
                  label="PV effettivo"
                  value={
                    balance.pv_effective_pct != null
                      ? `${Number(balance.pv_effective_pct).toFixed(1)}%`
                      : "—"
                  }
                  testId="impact-pv"
                />
                <ImpactStat
                  label="Scostamento"
                  value={
                    deltaPP != null ? `${deltaPP.toFixed(1)}pp` : "—"
                  }
                  testId="impact-delta"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Simple impact hint when no balancing */}
      {shippingImpact &&
        shippingImpact.onere === "noi" &&
        shippingImpact.costo > 0 &&
        !(Number(shipping.costo_stimato || 0) > 0) && (
          <div
            className="rounded px-2 py-1.5 text-[11px] flex items-start gap-1.5"
            style={{
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              color: "#92400E",
            }}
            data-testid="shipping-impact-note"
          >
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            {shippingImpact.note}
          </div>
        )}
    </section>
  );
}

function ImpactStat({ label, value, testId }) {
  return (
    <div className="rounded px-1.5 py-1 text-center bg-white"
         style={{ border: "1px solid var(--border-subtle)" }}>
      <div
        className="text-[9px] uppercase tracking-wide"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </div>
      <div
        className="tq-cell-mono text-[12px] font-semibold"
        style={{ color: "var(--text-primary)" }}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}
